import { motion, AnimatePresence } from 'framer-motion'
import { Scissors, X } from 'lucide-react'
import { Button } from '@/components/common'

/**
 * Two-button confirmation for pruning a branch (Round model v2).
 *
 * - "Prune just this branch" → soft-prune, re-parent direct children
 *   to the project root so they survive on their own.
 * - "Prune this branch and all descendants" → cascade prune.
 *
 * Pruning is always allowed (no gating). Pruned branches stay visible
 * in the tree but are excluded from OAI synthesis.
 */
export interface PruneConfirmModalProps {
  isOpen: boolean
  branchName: string
  onCancel: () => void
  onConfirm: (cascade: boolean) => void | Promise<void>
}

export const PruneConfirmModal = ({
  isOpen,
  branchName,
  onCancel,
  onConfirm,
}: PruneConfirmModalProps) => {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          onClick={onCancel}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.96, opacity: 0 }}
            className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={onCancel}
              aria-label="Close"
              className="absolute top-3 right-3 p-1 rounded text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                <Scissors className="w-5 h-5 text-red-600 dark:text-red-300" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 dark:text-white text-lg">
                  Prune branch?
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                  Pruning <span className="font-medium">"{branchName}"</span> will
                  hide it from problem synthesis. Its messages stay in the tree
                  for review, but OAI will treat the branch as if it never
                  existed.
                </p>
              </div>
            </div>

            <div className="mt-5 space-y-2">
              <Button
                variant="secondary"
                className="w-full justify-start"
                onClick={() => void onConfirm(false)}
              >
                <span className="text-left">
                  <span className="block font-medium">
                    Prune just this branch
                  </span>
                  <span className="block text-xs text-slate-500 dark:text-slate-400">
                    Direct children re-parent to root and survive on their own.
                  </span>
                </span>
              </Button>
              <Button
                variant="primary"
                className="w-full justify-start bg-red-600 hover:bg-red-700"
                onClick={() => void onConfirm(true)}
              >
                <span className="text-left">
                  <span className="block font-medium">
                    Prune this branch and all descendants
                  </span>
                  <span className="block text-xs text-red-100">
                    Every child branch is pruned too.
                  </span>
                </span>
              </Button>
            </div>

            <div className="mt-4 flex justify-end">
              <Button variant="ghost" onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default PruneConfirmModal
